import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { matchesSearch } from '@atlas/shared';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ExpandLessIcon,
  ExpandMoreIcon,
  SearchIcon,
  ClearIcon,
  Inventory2Icon,
  StorefrontIcon,
  AssignmentIcon,
  GroupIcon,
  AddIcon,
} from '@atlas/ui';
import {
  collapseTabGroups,
  filterNavForUser,
  flattenNavForUser,
  isNavItemActive,
  NAV_SECTIONS,
  sidebarKey,
  type NavItem,
  type NavSection,
} from '../../config/navConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useEffectiveRoles } from '@/lib/previewRole';
import { useHiddenFeatures } from '../../hooks/useHiddenFeatures';
import { useSidebarOrder } from '../../hooks/useSidebarOrder';
import { defaultSidebarOrder } from '../../config/personaSidebar';
import { NewEvaluationDialog } from './NewEvaluationDialog';
import {
  buildSegments,
  buildSidebarLayout,
  type DividerNode,
  type ItemNode,
  type LayoutNode,
  type LayoutSegment,
} from './sidebarLayout';
import { api, type SearchHit } from '../../services/api';

export const SIDE_MENU_WIDTH_EXPANDED = 280;
export const SIDE_MENU_WIDTH_COLLAPSED = 64;

const SECTION_COLLAPSED_KEY = 'atlas:side-menu-sections-collapsed';
const GROUP_COLLAPSED_KEY = 'atlas:side-menu-groups-collapsed';
const FOOTER_SECTION_IDS: ReadonlySet<string> = new Set(['admin', 'support']);

/**
 * Toggle to bring back section headers + accordion behavior. We currently
 * have too few items per section to justify the visual breaks, but the
 * machinery (collapsedSections state, per-section persistence, header
 * toggles) is preserved so we can flip this back on as the IA grows.
 */
const SHOW_SECTION_HEADERS: boolean = false;

const SEARCH_TYPE_ICONS: Record<SearchHit['type'], ReactNode> = {
  product: <Inventory2Icon fontSize="small" />,
  submission: <AssignmentIcon fontSize="small" />,
  user: <GroupIcon fontSize="small" />,
  // The same icon the Competitors nav entry uses, so a fixture row reads as
  // "somebody else's product" at a glance rather than as one of ours.
  fixture: <StorefrontIcon fontSize="small" />,
};

type SearchEntry =
  | { kind: 'nav'; item: NavItem }
  | { kind: 'hit'; hit: SearchHit };


interface SideMenuProps {
  /** Controls the temporary (mobile) drawer. Ignored when `permanent` is true. */
  open: boolean;
  /** Fires when the temporary drawer requests to close. */
  onClose: () => void;
  /** When true, render as a permanent side menu (md+). When false, render as a temporary overlay (xs/sm). */
  permanent: boolean;
  /** Collapsed state — only honored in permanent mode. */
  collapsed: boolean;
  /** Monotonic counter — when it changes, focus the search input. */
  searchFocusRequestId: number;
  /** Asks the parent to make the search input focus-able (expand rail / open drawer if needed). */
  onRequestSearchFocus: () => void;
}

/**
 * Persistent IA side menu. Renders sections driven by `navConfig.ts`,
 * with an inline search that takes over the body when the user types.
 * Above `md` it is a permanent drawer that can collapse to an icon-only rail;
 * below `md` it is a temporary overlay opened by the AppBar menu button.
 *
 * The menu is read-only here: users reorder items and manage named group
 * dividers on the profile page (`SidebarMenuSettings`). The one in-place
 * affordance is collapsing a group by clicking its header.
 */
export function SideMenu({
  open,
  onClose,
  permanent,
  collapsed,
  searchFocusRequestId,
  onRequestSearchFocus,
}: SideMenuProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hiddenFeatures } = useHiddenFeatures();
  const location = useLocation();
  const navigate = useNavigate();
  // Roles the nav renders for — the previewed role while an admin is viewing as
  // someone else, so role-gated entries (Admin) go too, not just hidden ones.
  const roles = useEffectiveRoles(user?.roles);
  const sections = filterNavForUser(NAV_SECTIONS, roles, hiddenFeatures, user?.divisions);
  const allItems = sections.flatMap((s) => s.items);

  const isRailCollapsed = permanent && collapsed;
  const width = isRailCollapsed ? SIDE_MENU_WIDTH_COLLAPSED : SIDE_MENU_WIDTH_EXPANDED;

  // `placement: 'create'` entries live in the New menu, not the list.
  const createItems = allItems.filter((i) => i.placement === 'create');
  const canEvaluate = allItems.some((i) => i.to === '/evaluations');
  const [newMenuAnchor, setNewMenuAnchor] = useState<HTMLElement | null>(null);
  const [pickingEvalProduct, setPickingEvalProduct] = useState(false);
  // A tab group shows as one entry (see `collapseTabGroups`), lit on any member's page.
  const isActive = (item: NavItem) =>
    item.tabGroup
      ? allItems.some(
          (m) => m.tabGroup === item.tabGroup && isNavItemActive(m, location.pathname, allItems),
        )
      : isNavItemActive(item, location.pathname, allItems);
  const mainSections = sections
    .filter((s) => !FOOTER_SECTION_IDS.has(s.id))
    .map((s) => ({ ...s, items: collapseTabGroups(s.items.filter((i) => i.placement !== 'create')) }))
    .filter((s) => s.items.length > 0);
  const footerSections = sections.filter((s) => FOOTER_SECTION_IDS.has(s.id));

  // -- User-ordered main nav with collapsible group dividers --
  // The scrollable body renders as one flat list (section headers are off), so
  // the saved order (nav paths + named divider tokens, see `sidebarLayout.ts`)
  // drives display order. Editing that order lives on the profile page; here we
  // only read it. Dividers render as collapsible group headers. Grouping/collapse
  // only applies in the expanded drawer — the icon rail falls back to the plain
  // sectioned rendering, and so does the (currently-off) section-header mode.
  const { order: sidebarOrder } = useSidebarOrder();
  const useFlatLayout = !isRailCollapsed && !SHOW_SECTION_HEADERS;
  const mainItems = useMemo(
    () => mainSections.flatMap((s) => s.items),
    [mainSections],
  );
  const layout = useMemo<LayoutNode<NavItem>[]>(
    () =>
      buildSidebarLayout(
        mainItems,
        // No saved order → the persona default (follows view-as-role preview).
        sidebarOrder.length > 0 ? sidebarOrder : defaultSidebarOrder(roles),
        sidebarKey,
      ),
    [mainItems, sidebarOrder, roles],
  );
  const segments = useMemo(() => buildSegments(layout), [layout]);

  // -- Group-collapse state (per-divider-id, persisted locally) --
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(GROUP_COLLAPSED_KEY);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });

  const toggleGroup = (id: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(GROUP_COLLAPSED_KEY, JSON.stringify([...next]));
      } catch {
        // ignore — Safari private mode etc.
      }
      return next;
    });
  };

  // -- Section accordion state --
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(SECTION_COLLAPSED_KEY);
      return new Set(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      return new Set();
    }
  });

  const toggleSection = (id: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        localStorage.setItem(SECTION_COLLAPSED_KEY, JSON.stringify([...next]));
      } catch {
        // ignore — Safari private mode etc.
      }
      return next;
    });
  };

  // -- Inline search state --
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const trimmed = query.trim();
  const isSearching = trimmed.length > 0;

  // Debounce input -> debounced query
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(trimmed), 150);
    return () => clearTimeout(timer);
  }, [trimmed]);

  // Fetch remote hits when debounced query has 2+ chars
  useEffect(() => {
    if (debounced.length < 2) {
      setHits([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.search
      .query(debounced)
      .then((data) => {
        if (!cancelled) setHits(data.results);
      })
      .catch(() => {
        if (!cancelled) setHits([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  // Focus the input when parent requests it (Cmd-K, mobile search icon, rail-icon search button)
  useEffect(() => {
    if (searchFocusRequestId > 0) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [searchFocusRequestId]);

  const navMatches = useMemo<NavItem[]>(() => {
    if (!trimmed) return [];
    return flattenNavForUser(NAV_SECTIONS, roles, hiddenFeatures, user?.divisions)
      .filter((i) => !i.comingSoon)
      .filter(
        (i) =>
          matchesSearch(`${i.label} ${i.description ?? ''}`, trimmed),
      )
      .slice(0, 6);
  }, [trimmed, roles, hiddenFeatures]);

  const entries: SearchEntry[] = useMemo(
    () => [
      ...navMatches.map((item) => ({ kind: 'nav' as const, item })),
      ...hits.map((hit) => ({ kind: 'hit' as const, hit })),
    ],
    [navMatches, hits],
  );

  // Clamp selection when entries shrink
  useEffect(() => {
    if (selectedIndex >= entries.length) {
      setSelectedIndex(Math.max(0, entries.length - 1));
    }
  }, [entries.length, selectedIndex]);

  function activateEntry(entry: SearchEntry) {
    const href = entry.kind === 'nav' ? entry.item.to : entry.hit.href;
    setQuery('');
    setSelectedIndex(0);
    if (!permanent) onClose();
    navigate(href);
  }

  function onSearchKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((i) => Math.min(entries.length - 1, i + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((i) => Math.max(0, i - 1));
    } else if (event.key === 'Enter') {
      const entry = entries[selectedIndex];
      if (entry) {
        event.preventDefault();
        activateEntry(entry);
      }
    } else if (event.key === 'Escape') {
      if (query) {
        event.preventDefault();
        setQuery('');
      }
    }
  }

  const searchTypeLabel: Record<SearchHit['type'], string> = {
    product: t('nav.searchTypes.product'),
    submission: t('nav.searchTypes.submission'),
    user: t('nav.searchTypes.user'),
    fixture: t('nav.searchTypes.fixture'),
  };

  const renderSection = (section: NavSection) => {
    const sectionCollapsed = collapsedSections.has(section.id);
    const showItems =
      !SHOW_SECTION_HEADERS || isRailCollapsed || !sectionCollapsed;

    return (
      <Box key={section.id} component="section" aria-label={section.label}>
        {SHOW_SECTION_HEADERS && !isRailCollapsed && (
          <ListItemButton
            onClick={() => toggleSection(section.id)}
            aria-expanded={!sectionCollapsed}
            aria-controls={`nav-section-${section.id}`}
            sx={{
              mx: 1,
              px: 1.25,
              py: 0.25,
              borderRadius: 1,
              color: 'text.secondary',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <ListItemText
              primary={section.label}
              primaryTypographyProps={{
                variant: 'overline',
                fontWeight: 600,
                letterSpacing: '0.08em',
                lineHeight: 2,
              }}
            />
            {sectionCollapsed ? (
              <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            ) : (
              <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            )}
          </ListItemButton>
        )}
        <Collapse
          in={showItems}
          timeout="auto"
          id={`nav-section-${section.id}`}
          unmountOnExit
        >
          <List dense disablePadding>
            {section.items.map((item) => (
              <NavItemRow
                key={item.to}
                item={item}
                active={isActive(item)}
                collapsed={isRailCollapsed}
                onNavigate={onClose}
              />
            ))}
          </List>
        </Collapse>
      </Box>
    );
  };

  const renderSearchResults = () => {
    if (entries.length === 0) {
      return (
        <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {trimmed.length < 2
              ? t('nav.searchMinChars')
              : loading
                ? t('nav.searchSearching')
                : t('nav.searchNoMatches')}
          </Typography>
        </Box>
      );
    }
    return (
      <List
        dense
        disablePadding
        role="listbox"
        aria-label={t('nav.searchResultsLabel')}
        sx={{ px: 1, py: 1 }}
      >
        {entries.map((entry, idx) => {
          const isSelected = idx === selectedIndex;
          if (entry.kind === 'nav') {
            const Icon = entry.item.icon;
            return (
              <ListItemButton
                key={`nav-${entry.item.to}`}
                selected={isSelected}
                onClick={() => activateEntry(entry)}
                onMouseEnter={() => setSelectedIndex(idx)}
                role="option"
                aria-selected={isSelected}
                sx={{
                  borderRadius: 1,
                  mb: 0.25,
                  px: 1.25,
                  '&.Mui-selected': { bgcolor: 'action.selected' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={entry.item.label}
                  secondary={entry.item.description}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                />
              </ListItemButton>
            );
          }
          const hit = entry.hit;
          return (
            <ListItemButton
              key={`hit-${hit.type}-${hit.id}`}
              selected={isSelected}
              onClick={() => activateEntry(entry)}
              onMouseEnter={() => setSelectedIndex(idx)}
              role="option"
              aria-selected={isSelected}
              sx={{
                borderRadius: 1,
                mb: 0.25,
                px: 1.25,
                '&.Mui-selected': { bgcolor: 'action.selected' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                {SEARCH_TYPE_ICONS[hit.type]}
              </ListItemIcon>
              <ListItemText
                primary={hit.label}
                secondary={hit.description}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
                secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
              />
              <Chip
                size="small"
                label={searchTypeLabel[hit.type]}
                variant="outlined"
                sx={{ ml: 0.5, height: 18, fontSize: '0.625rem' }}
              />
            </ListItemButton>
          );
        })}
      </List>
    );
  };

  /** Render one group segment: a collapsible header (if any) wrapping its items. */
  const renderSegment = (segment: LayoutSegment<NavItem>) => {
    const renderItem = (node: ItemNode<NavItem>) => (
      <NavItemRow
        key={node.key}
        item={node.item}
        active={isActive(node.item)}
        collapsed={isRailCollapsed}
        onNavigate={onClose}
      />
    );

    // Items above the first divider — always visible, no header.
    if (!segment.divider) {
      if (segment.items.length === 0) return null;
      return (
        <List key="__ungrouped" dense disablePadding>
          {segment.items.map(renderItem)}
        </List>
      );
    }

    const groupId = segment.divider.id;
    // A group collapses on request even when it holds the current page. The
    // previous `&& !hasActive` guard kept that group pinned open, which made
    // its chevron a dead click — the toggle was still recorded and persisted,
    // so the group sprang shut later, on the first navigation away.
    const isCollapsed = collapsedGroups.has(groupId);

    return (
      <Box key={segment.divider.key} component="section">
        <GroupHeaderRow
          node={segment.divider}
          collapsed={isCollapsed}
          onToggle={() => toggleGroup(groupId)}
        />
        <Collapse in={!isCollapsed} timeout="auto" id={`nav-group-${groupId}`} unmountOnExit>
          <List dense disablePadding>
            {segment.items.map(renderItem)}
          </List>
        </Collapse>
      </Box>
    );
  };

  const content = (
    <Box
      id="primary-navigation"
      role="navigation"
      aria-label={t('nav.primaryNavLabel')}
      sx={{
        width,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRight: permanent ? 1 : 0,
        borderColor: 'divider',
        overflow: 'hidden',
        transition: (theme) =>
          theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
      }}
    >
      {/* Search — its own row, just above the nav */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isRailCollapsed ? 'center' : 'stretch',
          px: isRailCollapsed ? 0 : 1,
          py: 0.5,
          minHeight: 48,
        }}
      >
        {isRailCollapsed ? (
          <Tooltip title={t('nav.searchShortcutTooltip')} placement="right" arrow>
            <IconButton
              size="small"
              onClick={onRequestSearchFocus}
              aria-label={t('nav.openSearch')}
              aria-keyshortcuts="Control+K Meta+K"
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <TextField
            inputRef={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={t('nav.searchPlaceholder')}
            autoComplete="off"
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment:
                loading ? (
                  <InputAdornment position="end">
                    <CircularProgress size={14} />
                  </InputAdornment>
                ) : query ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setQuery('');
                        inputRef.current?.focus();
                      }}
                      aria-label={t('nav.clearSearch')}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <Box
                      component="kbd"
                      sx={{
                        px: 0.5,
                        py: 0,
                        fontSize: '0.6875rem',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 0.5,
                        color: 'text.disabled',
                        bgcolor: 'background.default',
                        fontFamily: 'inherit',
                      }}
                    >
                      ⌘K
                    </Box>
                  </InputAdornment>
                ),
              'aria-label': t('nav.searchAriaLabel'),
              'aria-keyshortcuts': 'Control+K Meta+K',
            }}
            sx={{ flex: 1 }}
          />
        )}
      </Box>

      {(createItems.length > 0 || canEvaluate) && (
        <Box sx={{ px: isRailCollapsed ? 0 : 1, pt: 1, display: 'flex', justifyContent: 'center' }}>
          {isRailCollapsed ? (
            // "New" is hardcoded English, like the PDLC Board item: a new nav
            // key obliges regenerating nine locales.
            <Tooltip title="New" placement="right" arrow>
              <IconButton
                size="small"
                onClick={(e) => setNewMenuAnchor(e.currentTarget)}
                aria-label="New"
                aria-haspopup="menu"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              size="small"
              fullWidth
              startIcon={<AddIcon />}
              onClick={(e) => setNewMenuAnchor(e.currentTarget)}
              aria-haspopup="menu"
            >
              New
            </Button>
          )}
        </Box>
      )}
      <Menu
        anchorEl={newMenuAnchor}
        open={newMenuAnchor !== null}
        onClose={() => setNewMenuAnchor(null)}
      >
        {canEvaluate && (
          <MenuItem
            onClick={() => {
              setNewMenuAnchor(null);
              setPickingEvalProduct(true);
            }}
          >
            <ListItemIcon>
              <AssignmentIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Evaluation" />
          </MenuItem>
        )}
        {createItems.map((item) => {
          const Icon = item.icon;
          return (
            <MenuItem
              key={item.to}
              component={Link}
              to={item.to}
              onClick={() => {
                setNewMenuAnchor(null);
                onClose();
              }}
            >
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </MenuItem>
          );
        })}
      </Menu>
      <NewEvaluationDialog
        open={pickingEvalProduct}
        onClose={() => {
          setPickingEvalProduct(false);
          onClose();
        }}
      />

      {isSearching ? (
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {renderSearchResults()}
        </Box>
      ) : (
        <>
          {/* Main (scrollable) nav. Flat, collapsible-group list when expanded;
              falls back to the sectioned rail rendering when collapsed. */}
          <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1 }}>
            {useFlatLayout
              ? segments.map(renderSegment)
              : mainSections.map(renderSection)}
          </Box>

          {/* Footer sections — pinned to bottom */}
          {footerSections.length > 0 && (
            <Box
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                py: 1,
                overflowX: 'hidden',
              }}
            >
              {footerSections.map(renderSection)}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  if (permanent) {
    return (
      <Drawer
        variant="permanent"
        open
        sx={{
          width,
          flexShrink: 0,
          transition: (theme) =>
            theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
            top: 56,
            height: 'calc(100% - 56px)',
            overflowX: 'hidden',
            transition: (theme) =>
              theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width: SIDE_MENU_WIDTH_EXPANDED,
          boxSizing: 'border-box',
        },
      }}
    >
      {content}
    </Drawer>
  );
}

interface NavItemRowProps {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onNavigate: () => void;
}

function NavItemRow({ item, active, collapsed, onNavigate }: NavItemRowProps) {
  const { t } = useTranslation();
  const Icon = item.icon;

  const button = (
    <ListItemButton
      component={item.comingSoon ? 'div' : Link}
      to={item.comingSoon ? undefined : item.to}
      disabled={item.comingSoon}
      onClick={item.comingSoon ? undefined : onNavigate}
      selected={active}
      sx={{
        flex: 1,
        minWidth: 0,
        mx: 1,
        px: collapsed ? 1 : 1.25,
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius: 1,
        color: active ? 'primary.main' : 'text.primary',
        '&.Mui-selected': {
          bgcolor: 'action.selected',
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
      aria-current={active ? 'page' : undefined}
    >
      <ListItemIcon
        sx={{
          minWidth: collapsed ? 0 : 32,
          justifyContent: 'center',
          color: active ? 'primary.main' : 'text.secondary',
        }}
      >
        <Icon fontSize="small" />
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            variant: 'body2',
            fontWeight: active ? 600 : 500,
          }}
        />
      )}
    </ListItemButton>
  );

  const row = (
    <ListItem disablePadding sx={{ display: 'block' }}>
      {button}
    </ListItem>
  );

  if (collapsed && !item.comingSoon) {
    return (
      <Tooltip title={item.label} placement="right" arrow>
        {row}
      </Tooltip>
    );
  }
  if (item.comingSoon) {
    return (
      <Tooltip
        title={collapsed ? t('nav.comingSoonItem', { label: item.label }) : t('nav.comingSoon')}
        placement="right"
        arrow
      >
        <span>{row}</span>
      </Tooltip>
    );
  }
  return row;
}

interface GroupHeaderRowProps {
  node: DividerNode;
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * A named group divider in the read-only sidebar. Renders as a labeled rule
 * that toggles the group's items open/closed when clicked. Editing group names
 * and order happens on the profile page, not here.
 */
function GroupHeaderRow({ node, collapsed, onToggle }: GroupHeaderRowProps) {
  const { t } = useTranslation();
  const displayLabel = node.label.trim() || t('nav.dividerUnnamed');

  return (
    <ListItemButton
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-controls={`nav-group-${node.id}`}
      sx={{
        mx: 1,
        px: 1.25,
        py: 0.25,
        borderRadius: 1,
        gap: 1,
        color: 'text.secondary',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Typography
        variant="overline"
        noWrap
        sx={{
          fontWeight: 600,
          letterSpacing: '0.08em',
          lineHeight: 1.6,
          color: node.label.trim() ? 'text.secondary' : 'text.disabled',
        }}
      >
        {displayLabel}
      </Typography>
      <Divider sx={{ flex: 1 }} />
      {collapsed ? (
        <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      ) : (
        <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      )}
    </ListItemButton>
  );
}

export default SideMenu;
